import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

type Translations = {
  [key: string]: any;
};

type LanguageContextType = {
  language: string;
  translations: Translations;
  setLanguage: (lang: string) => void;
  t: (section: string, key: string) => string;
};

const defaultContext: LanguageContextType = {
  language: 'en',
  translations: {},
  setLanguage: () => {},
  t: () => '',
};

const LanguageContext = createContext<LanguageContextType>(defaultContext);

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<string>('en'); // Default to English
  const [translations, setTranslations] = useState<Translations>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load translations from JSON file
  useEffect(() => {
    fetch('/locales.json')
      .then(response => response.json())
      .then(data => {
        setTranslations(data);
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error loading translations:', error);
        setIsLoading(false);
      });
  }, []);

  // Load saved language preference from localStorage
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'pl')) {
      setLanguageState(savedLanguage);
      // Update HTML and body lang attributes on initial load
      document.documentElement.lang = savedLanguage;
      document.body.lang = savedLanguage;
    }
  }, []);

  // Function to change language
  const setLanguage = (lang: string) => {
    if (lang === 'en' || lang === 'pl') {
      localStorage.setItem('language', lang);
      setLanguageState(lang);
      
      // Update the HTML and body lang attributes to match the selected language
      document.documentElement.lang = lang;
      document.body.lang = lang;
    }
  };

  // Translation function
  const t = (section: string, key: string): string => {
    if (isLoading) return '';
    
    try {
      // Try to get translation in current language
      const translation = translations[language]?.[section]?.[key];
      
      // If translation exists, return it
      if (translation) return translation;
      
      // If not found in current language and it's not English, try English as fallback
      if (language !== 'en') {
        const fallbackTranslation = translations['en']?.[section]?.[key];
        if (fallbackTranslation) return fallbackTranslation;
      }
      
      // If still not found, return the key itself
      return key;
    } catch (error) {
      console.error(`Translation error for ${section}.${key}:`, error);
      return key;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, translations, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);